/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Periods } from 'cio/utils/metrics/common';
import EnvironmentModel from 'cio/models/environment';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import colorLabels from 'cio/utils/color-labels';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import MetricsService from 'cio/services/metrics';
import { SplitState } from 'cio/models/action';
import AnalyticsService from 'cio/services/analytics';
import { SortDirection } from 'cio/utils/sort';
import { DashboardTestSplitAction, DashboardTestNewsletter } from 'cio/utils/metrics/models/dashboard-tests';
import { TimeRange } from 'cio/utils/time';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';

interface DashboardPageTestsCardArgs {
  environmentModel: EnvironmentModel;
  timeRange: TimeRange;
  resolution: Periods;
  onTrackNavigate: (data: Record<string, any>) => unknown;
}

export default class DashboardPageTestsCard extends Component<DashboardPageTestsCardArgs> {
  @service analytics!: AnalyticsService;
  @service('metrics') metricsService!: MetricsService;

  @tracked activeTab: 'campaigns' | 'newsletters' = 'campaigns';

  COLOR_LABELS = colorLabels;

  campaignArrayItems = [...new Array(10)];

  @tracked sortBy = 'opened';

  get sortByItems() {
    if (this.activeTab === 'campaigns') {
      return [
        {
          label: 'Opened',
          value: 'opened',
          sortBy: 'opened.rate',
          sortDirection: SortDirection.Descending,
        },
        {
          label: 'Clicked',
          value: 'clicked',
          sortBy: 'clicked.rate',
          sortDirection: SortDirection.Descending,
        },
        {
          label: 'Converted',
          value: 'converted',
          sortBy: 'converted.rate',
          sortDirection: SortDirection.Descending,
        },
        {
          label: 'Most recent',
          value: 'most_recent',
          sortBy: 'started',
          sortDirection: SortDirection.Descending,
        },
        {
          label: 'Longest running',
          value: 'longest_running',
          sortBy: 'started',
          sortDirection: SortDirection.Ascending,
        },
      ];
    } else {
      return [
        {
          label: 'Most recent',
          value: 'most_recent',
          sortBy: 'sentAt',
          sortDirection: SortDirection.Descending,
        },
        {
          label: 'Longest running',
          value: 'longest_running',
          sortBy: 'sentAt',
          sortDirection: SortDirection.Ascending,
        },
      ];
    }
  }

  get sortByItem() {
    return this.sortByItems.find((item) => item.value === this.sortBy);
  }

  @tracked splitActions: DashboardTestSplitAction[] = [];
  @tracked newsletters: DashboardTestNewsletter[] = [];

  @action
  onInsert() {
    this.fetchMetrics.perform();

    if (!isDeliveryMeasureEnabledForChannel('opened', 'email')) {
      this.sortBy = 'clicked';
    }
  }

  fetchMetrics = task({ restartable: true }, async () => {
    let results = await this.metricsService.fetchDashboardTestMetrics(
      this.args.environmentModel.id,
      this.args.timeRange,
      this.args.resolution,
    );

    this.splitActions = results.splitActions.filter((i) => i.splitState === SplitState.Running);
    this.newsletters = results.newsletters;
  });

  @action
  onTabChanged(tab: 'campaigns' | 'newsletters') {
    this.activeTab = tab;

    // If the current sort selection doesn't exist after switching tabs (because
    // the tabs have different sort by lists), select the first available item
    if (!this.sortByItems.some((item) => item.value === this.sortBy)) {
      this.sortBy = this.sortByItems[0].value;
    }

    this.trackDisplayChanged('tab');
  }

  @action
  onSortByChanged(sortBy: string) {
    this.sortBy = sortBy;

    this.trackDisplayChanged('sortBy');
  }

  @action
  trackDisplayChanged(editType: string) {
    this.analytics.track('dashboard', 'tests_card_display_changed', {
      tab: this.activeTab,
      sortBy: this.sortBy,
      editType,
    });
  }

  @action
  trackNavigate(destination: string) {
    this.args.onTrackNavigate({
      card: 'tests',
      destination,
    });
  }
}
